import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { useFormatting } from '@pflegenavi/frontend/localization';
import {
  ABSOLUTE_MAXIMUM_PAYMENT_AMOUNT,
  ABSOLUTE_MINIMUM_PAYMENT_AMOUNT,
} from '@pflegenavi/shared/api';
import type {
  IManualPaymentFormValues,
  UseManualPaymentValidationSchemaProps,
} from './types';

export const useManualPaymentValidationSchema = ({
  balanceInfo,
  familyMember,
  residentBalance,
}: UseManualPaymentValidationSchemaProps): {
  initialValues: IManualPaymentFormValues;
  validationSchema: Yup.ObjectSchema<any>;
} => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();

  const topUpAmount =
    (balanceInfo?.residentConfiguration?.topUpAmount ?? 0) * 100;

  const minAmount = balanceInfo
    ? balanceInfo.nursingHomeConfiguration.minPaymentAmount
    : ABSOLUTE_MINIMUM_PAYMENT_AMOUNT;
  const maxAmount = balanceInfo
    ? balanceInfo.nursingHomeConfiguration.maxPaymentAmount
    : ABSOLUTE_MAXIMUM_PAYMENT_AMOUNT;
  const initialAmount =
    topUpAmount !== undefined && residentBalance !== undefined
      ? Math.min(maxAmount, Math.max(minAmount, topUpAmount - residentBalance))
      : minAmount;

  const initialValues: IManualPaymentFormValues = useMemo(() => {
    if (familyMember) {
      return {
        amount: initialAmount,
        balance: residentBalance ? residentBalance : 0,
      };
    }
    return {
      amount: initialAmount,
      balance: 0,
    };
  }, [familyMember, initialAmount, residentBalance]);

  const validationSchema = Yup.object().shape({
    amount: Yup.number()
      .required(t('fm-triggered-payments.amount-screen.error-required-amount'))
      .min(
        minAmount,
        t('fm-triggered-payments.amount-screen.error-minimum-amount', {
          amount: fCurrencyCents(minAmount),
        })
      )
      .max(
        maxAmount,
        t('fm-triggered-payments.amount-screen.error-maximum-amount', {
          amount: fCurrencyCents(maxAmount),
        })
      ),
  });

  return {
    initialValues,
    validationSchema,
  };
};
